import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SubLink {
  key: string;
  defaultLabel: string;
  href: string;
}

interface NavLink {
  key: string;
  defaultLabel: string;
  href: string;
  sub?: SubLink[];
}

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpandedDoc, setMobileExpandedDoc] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  const currentLang = i18n.language || 'kk';

  const navLinks: NavLink[] = [
    { key: 'nav.home', defaultLabel: currentLang === 'en' ? 'Home' : currentLang === 'ru' ? 'Главная' : 'Басты бет', href: '/' },
    { key: 'nav.about', defaultLabel: currentLang === 'en' ? 'About us' : currentLang === 'ru' ? 'О нас' : 'Біз туралы', href: '#about' },
    { key: 'nav.disciplines', defaultLabel: currentLang === 'en' ? 'Disciplines' : currentLang === 'ru' ? 'Направления' : 'Бағыттар', href: '#disciplines' },
    { key: 'nav.goals', defaultLabel: currentLang === 'en' ? 'Goals' : currentLang === 'ru' ? 'Цели' : 'Мақсаттар', href: '#goals' },
    { key: 'nav.events', defaultLabel: currentLang === 'en' ? 'Events' : currentLang === 'ru' ? 'Мероприятия' : 'Іс-шаралар', href: '#events' },
    {
      key: 'nav.documents',
      defaultLabel: currentLang === 'en' ? 'Documentation' : currentLang === 'ru' ? 'Документация' : 'Құжаттама',
      href: '/documents',
      sub: [
        { key: 'nav.documents', defaultLabel: currentLang === 'en' ? 'Documentation' : currentLang === 'ru' ? 'Документация' : 'Құжаттама', href: '/documents' },
        { key: 'nav.docs_patriot', defaultLabel: currentLang === 'en' ? '«Patriot» Documents' : currentLang === 'ru' ? 'Документация «Patriot»' : '«Patriot» құжаттамасы', href: '/documents/patriot' },
      ],
    },
    { key: 'nav.contacts', defaultLabel: currentLang === 'en' ? 'Contacts' : currentLang === 'ru' ? 'Контакты' : 'Байланыс', href: '#contacts' },
  ];

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    setOpenDropdown(null);
    if (href.startsWith('#')) {
      if (isHome) {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-950/95 backdrop-blur-md border-b border-background-800/60 shadow-xl'
            : 'bg-gradient-to-b from-background-950/90 via-background-950/40 to-transparent'
        }`}
      >
        <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-20 md:h-22">
            {/* Logo & Brand Identity */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center flex-shrink-0">
                <img
                  src="/favicon.png"
                  alt="Federation Logo"
                  className="w-full h-full object-contain filter invert"
                />
              </div>
              <div className="hidden sm:flex flex-col justify-center">
                <span className="font-heading text-foreground-100 text-xs md:text-sm font-semibold tracking-wide uppercase leading-tight group-hover:text-accent-400 transition-colors">
                  {t('nav.title', currentLang.startsWith('en')
                    ? 'National Federation'
                    : currentLang.startsWith('ru')
                    ? 'Национальная Федерация'
                    : 'Ұлттық Федерация')}
                </span>
                <span className="text-foreground-700 text-[0.6rem] md:text-xs tracking-widest uppercase mt-0.5">
                  {t('nav.subtitle', currentLang.startsWith('en')
                    ? 'of military applied sports'
                    : currentLang.startsWith('ru')
                    ? 'военно-прикладных видов спорта'
                    : 'әскери-қолданбалы спорт түрлері бойынша')}
                </span>
              </div>
            </Link>

            {/* Desktop Nav & Language Switcher */}
            <div className="hidden lg:flex items-center gap-4" ref={dropdownRef}>
              <div className="flex items-center gap-1">
                {navLinks.map((link) => {
                  const label = t(link.key, link.defaultLabel);
                  if (link.sub) {
                    const isOpen = openDropdown === link.key;
                    return (
                      <div key={link.key} className="relative">
                        <button
                          onClick={() => setOpenDropdown(isOpen ? null : link.key)}
                          className="flex items-center gap-1 px-3.5 py-2 text-xs tracking-widest uppercase text-foreground-300 hover:text-accent-400 transition-colors duration-200 font-label whitespace-nowrap cursor-pointer rounded hover:bg-background-800/40"
                        >
                          <span>{label}</span>
                          <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isOpen ? 'rotate-180 text-accent-500' : 'text-foreground-500'}`} />
                        </button>
                        {isOpen && (
                          <div className="absolute top-full left-0 mt-2 min-w-[240px] bg-background-950/98 backdrop-blur-xl border border-background-700/60 rounded-lg overflow-hidden shadow-2xl z-50">
                            {link.sub.map((sub) => (
                              <Link
                                key={sub.href}
                                to={sub.href}
                                onClick={() => setOpenDropdown(null)}
                                className="block px-5 py-3 text-xs tracking-wider text-foreground-300 hover:text-accent-400 hover:bg-background-800/80 transition-colors duration-200 font-label whitespace-nowrap border-b border-background-800/40 last:border-0"
                              >
                                {t(sub.key, sub.defaultLabel)}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }

                  if (link.href.startsWith('#')) {
                    return (
                      <a
                        key={link.key}
                        href={isHome ? link.href : `/${link.href}`}
                        onClick={() => handleNavClick(link.href)}
                        className="px-3.5 py-2 text-xs tracking-widest uppercase text-foreground-300 hover:text-accent-400 transition-colors duration-200 font-label whitespace-nowrap rounded hover:bg-background-800/40"
                      >
                        {label}
                      </a>
                    );
                  }

                  return (
                    <Link
                      key={link.key}
                      to={link.href}
                      onClick={() => setOpenDropdown(null)}
                      className="px-3.5 py-2 text-xs tracking-widest uppercase text-foreground-300 hover:text-accent-400 transition-colors duration-200 font-label whitespace-nowrap rounded hover:bg-background-800/40"
                    >
                      {label}
                    </Link>
                  );
                })}
              </div>

              {/* Language Switcher */}
              <div className="flex items-center p-1 bg-background-900/90 rounded-lg border border-background-700/60">
                <button
                  onClick={() => handleLanguageChange('kk')}
                  className={`px-2.5 py-1 text-[11px] font-label font-bold uppercase rounded transition-all duration-200 cursor-pointer ${
                    currentLang.startsWith('kk')
                      ? 'bg-accent-500 text-background-950 shadow-sm'
                      : 'text-foreground-400 hover:text-foreground-100'
                  }`}
                >
                  ҚАЗ
                </button>
                <button
                  onClick={() => handleLanguageChange('ru')}
                  className={`px-2.5 py-1 text-[11px] font-label font-bold uppercase rounded transition-all duration-200 cursor-pointer ${
                    currentLang.startsWith('ru')
                      ? 'bg-accent-500 text-background-950 shadow-sm'
                      : 'text-foreground-400 hover:text-foreground-100'
                  }`}
                >
                  РУС
                </button>
                <button
                  onClick={() => handleLanguageChange('en')}
                  className={`px-2.5 py-1 text-[11px] font-label font-bold uppercase rounded transition-all duration-200 cursor-pointer ${
                    currentLang.startsWith('en')
                      ? 'bg-accent-500 text-background-950 shadow-sm'
                      : 'text-foreground-400 hover:text-foreground-100'
                  }`}
                >
                  ENG
                </button>
              </div>
            </div>

            {/* Mobile Actions (Language + Toggle) */}
            <div className="lg:hidden flex items-center gap-2">
              <div className="flex items-center p-0.5 bg-background-900/90 rounded-lg border border-background-700/60">
                <button
                  onClick={() => handleLanguageChange('kk')}
                  className={`px-2 py-1 text-[10px] font-label font-bold uppercase rounded transition-colors ${
                    currentLang.startsWith('kk') ? 'bg-accent-500 text-background-950' : 'text-foreground-400'
                  }`}
                >
                  ҚАЗ
                </button>
                <button
                  onClick={() => handleLanguageChange('ru')}
                  className={`px-2 py-1 text-[10px] font-label font-bold uppercase rounded transition-colors ${
                    currentLang.startsWith('ru') ? 'bg-accent-500 text-background-950' : 'text-foreground-400'
                  }`}
                >
                  РУС
                </button>
                <button
                  onClick={() => handleLanguageChange('en')}
                  className={`px-2 py-1 text-[10px] font-label font-bold uppercase rounded transition-colors ${
                    currentLang.startsWith('en') ? 'bg-accent-500 text-background-950' : 'text-foreground-400'
                  }`}
                >
                  ENG
                </button>
              </div>

              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="w-10 h-10 flex items-center justify-center text-foreground-100 rounded-lg bg-background-900/70 border border-background-700/50 cursor-pointer hover:border-accent-500/50 transition-colors"
                aria-label="Menu"
              >
                <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl text-accent-400`} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-background-950/98 backdrop-blur-2xl transition-all duration-300 lg:hidden ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex flex-col items-center justify-center h-full gap-6 px-6">
          <div className="w-16 h-16 rounded-full bg-background-900 border border-accent-500/40 p-2 mb-2">
            <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain filter invert" />
          </div>

          <div className="text-center mb-4">
            <p className="font-heading text-lg font-bold text-foreground-100 tracking-widest uppercase">
              {currentLang.startsWith('en') ? 'National Federation' : currentLang.startsWith('ru') ? 'Национальная Федерация' : 'Ұлттық Федерация'}
            </p>
            <p className="text-xs text-foreground-600 tracking-wider uppercase font-label">
              {currentLang.startsWith('en') ? 'of military applied sports' : currentLang.startsWith('ru') ? 'военно-прикладных видов спорта' : 'әскери-қолданбалы спорт түрлері бойынша'}
            </p>
          </div>

          {navLinks.map((link) => {
            const label = t(link.key, link.defaultLabel);
            if (link.sub) {
              return (
                <div key={link.key} className="flex flex-col items-center w-full max-w-xs">
                  <button
                    onClick={() => setMobileExpandedDoc(!mobileExpandedDoc)}
                    className="flex items-center justify-center gap-2 text-base font-heading tracking-widest uppercase text-foreground-100 hover:text-accent-400 transition-colors cursor-pointer w-full py-1"
                  >
                    <span>{label}</span>
                    <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileExpandedDoc ? 'rotate-180 text-accent-500' : ''}`} />
                  </button>
                  {mobileExpandedDoc && (
                    <div className="flex flex-col items-center gap-2 mt-2 w-full bg-background-900/60 py-2 rounded-lg border border-background-800/60">
                      {link.sub.map((sub) => (
                        <Link
                          key={sub.href}
                          to={sub.href}
                          onClick={() => setMobileOpen(false)}
                          className="text-xs font-label tracking-widest uppercase text-accent-400 hover:text-accent-300 py-1"
                        >
                          {t(sub.key, sub.defaultLabel)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            if (link.href.startsWith('#')) {
              return (
                <a
                  key={link.key}
                  href={isHome ? link.href : `/${link.href}`}
                  onClick={() => handleNavClick(link.href)}
                  className="text-base font-heading tracking-widest uppercase text-foreground-100 hover:text-accent-400 transition-colors py-1"
                >
                  {label}
                </a>
              );
            }

            return (
              <Link
                key={link.key}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className="text-base font-heading tracking-widest uppercase text-foreground-100 hover:text-accent-400 transition-colors py-1"
              >
                {label}
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}